import { SelectOptionProps } from '@src/quince-components/src/atoms/select/Select'
import { ProductOptionDisplayType } from '@src/quince-site-common/src/domains/ProductOption'
import getColorFromUrl from '@src/quince-site-common/src/utils/extractColorFromURL'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { isEqual, mapValues } from '@src/utils/functions/utils'

import { FILTER_VERSION_CONFIG, FilterVersionConfig } from '../filter-version-config'

import { fromV2Key, toV2Key } from './filter-key-aliases'

export interface IFilterConfig {
  readonly columnsToShow: number
  readonly label: string
  readonly type: string
}

export interface IProductTypeFilter {
  readonly title: string
  readonly slug: string
}

export class FilterConfig {
  readonly columnsToShow: number
  readonly label: string
  readonly type: string

  constructor({ columnsToShow, label, type }: IFilterConfig) {
    this.columnsToShow = columnsToShow
    this.label = label
    this.type = type
    return {
      ...this,
    }
  }
}

export interface IFilter {
  readonly name: string
  readonly values: (FilterOptions | string)[]
  readonly config: FilterConfig
}

export interface UpdateQueryParams {
  filter?:
    | { type: 'reset' }
    | { type: 'remove'; name: FilterKeys; value: string }
    | { type: 'toggle'; name: FilterKeys; value: string; label: string }
    | { type: 'removeAll'; name: FilterKeys; label: string; value: string }
    | { type: 'overwrite'; updatedFilters: SelectedFilter }
  sort?: { type: 'reset' } | { type: 'toggle'; value: string }
}

export class Filter implements IFilter {
  readonly name: string
  readonly values: (FilterOptions | string)[]
  readonly config: FilterConfig

  constructor({ name, values, config }: IFilter) {
    this.name = name
    this.values = values
    this.config = config
    return {
      ...this,
    }
  }

  static makeSelectFilterOptionFromValue(value: FilterOptions | string): SelectOptionProps {
    if (typeof value == 'string') {
      return {
        label: value,
        value: value,
      }
    } else {
      return FilterOptions.makeSelectFilterOption(value)
    }
  }
}

export interface IFilterOption {
  readonly name: string
  readonly displayName?: string
  readonly value: string
  readonly displayType?: ProductOptionDisplayType
  readonly hex?: string
  readonly nameForSubcollectionPill?: string
  readonly imgURL?: string
}

export class FilterOptions implements IFilterOption {
  readonly name: string
  readonly displayName: string
  readonly value: string
  readonly displayType: ProductOptionDisplayType | undefined
  readonly hex: string | undefined = undefined
  readonly imgURL: string | undefined = undefined
  readonly nameForSubcollectionPill: string | undefined = undefined

  constructor({
    name,
    value,
    displayType,
    displayName,
    hex = undefined,
    imgURL = undefined,
    nameForSubcollectionPill,
  }: IFilterOption) {
    this.name = name
    this.value = value
    this.displayName = displayName ?? name
    this.displayType = displayType
    this.hex = hex
    this.imgURL = imgURL
    this.nameForSubcollectionPill = nameForSubcollectionPill
    return {
      ...this,
    }
  }

  static makeSelectFilterOption(filterOptions: FilterOptions): SelectOptionProps {
    return {
      label: filterOptions.displayName || filterOptions.name,
      value: filterOptions.value || filterOptions.name,
      hex: filterOptions.hex,
      imgURL: filterOptions.imgURL,
    }
  }
}

export type ISelectedFilter = Readonly<Record<string, string[] | undefined>>

export type FilterKeys = keyof ISelectedFilter

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class SelectedFilter implements ISelectedFilter {
  [key: string]: string[] | undefined

  constructor(selectedFilter: ISelectedFilter) {
    for (const [key, value] of Object.entries(selectedFilter)) {
      this[key] = value
    }
    return { ...this }
  }

  static isAnyFilterSelected(selectedFilter: SelectedFilter) {
    return !!Object.keys(selectedFilter).find(x =>
      SelectedFilter.isFieldPresent(SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, x)),
    )
  }

  private static isFieldPresent(selectedField: string[] | undefined): boolean {
    return !!(selectedField && selectedField.length > 0)
  }

  public static getSelectedValuesAsList(selectedFilter: SelectedFilter): string[] {
    return Object.keys(selectedFilter)
      .map(key => SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, key))
      .flat()
  }

  public static getSelectedFilterValuesByFilterName(
    selectedFilter: SelectedFilter,
    filterName: FilterKeys,
    shouldEncode = false,
  ): string[] {
    const selectedFilterList = selectedFilter[filterName] ?? []

    if (shouldEncode) {
      return selectedFilterList.map((filter: string) => encodeURIComponent(filter))
    }

    return selectedFilterList
  }

  public static removeValueFromSelectedFilter(selectedFilter: SelectedFilter, value: string, filterName?: FilterKeys) {
    if (filterName) {
      const currentFilterValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName)
      const updatedFilterValues = currentFilterValues.filter(item => item !== value)
      return new SelectedFilter({
        ...selectedFilter,
        [filterName]: updatedFilterValues,
      })
    } else {
      const sanitizedFilter = mapValues(selectedFilter, (arr?: string[]) => {
        const filteredArray = arr?.filter(x => x !== value)
        return filteredArray?.length === 0 ? undefined : filteredArray
      })
      return new SelectedFilter(sanitizedFilter)
    }
  }

  public static removeAllValuesFromSelectedFilter(selectedFilter: SelectedFilter, filterName: FilterKeys) {
    return new SelectedFilter({
      ...selectedFilter,
      [filterName]: [],
    })
  }

  public static isFilterValueSelected(selectedFilter: SelectedFilter, filterName: FilterKeys, value: string) {
    const currentFilterValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName)
    return currentFilterValues.includes(value)
  }

  public static addValueToSelectedFilter(selectedFilter: SelectedFilter, filterName: FilterKeys, value: string) {
    const updatedFilterValues = SelectedFilter.isSingleSelectFilter(filterName)
      ? [value]
      : [...SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName), value]

    return new SelectedFilter({
      ...selectedFilter,
      [filterName]: updatedFilterValues,
    })
  }

  public static toggleValueFromSelectedFilter(selectedFilter: SelectedFilter, filterName: FilterKeys, value: string) {
    const currentFilterValues = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, filterName)
    if (currentFilterValues.includes(value)) {
      trackEvent(TELEMETRY_EVENT_NAMES.INDIVIDUAL_FILTERS_CLEARED, {
        filter_key: filterName,
        filter_value: value,
      })
      return SelectedFilter.removeValueFromSelectedFilter(selectedFilter, value, filterName)
    } else {
      return SelectedFilter.addValueToSelectedFilter(selectedFilter, filterName, value)
    }
  }

  public static getUpdatedSelectedFilterStringForWindowURL(selectedFilter: SelectedFilter) {
    const filterString = Object.keys(selectedFilter)
      .map(key => {
        const values = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, key, true)
        if (values.length > 0) {
          return `${key}=${values.join(',')}`
        }
        return undefined
      })
      .filter(value => value !== undefined)
      .join('&')

    return filterString
  }

  public static isSingleSelectFilter(filterName: FilterKeys): boolean {
    return filterName === String(EFilterKeys.PRODUCT_TYPE) || filterName === String(EFilterKeys.SUBCOLLECTIONS)
  }

  public static getSelectedFilterFromQueryParams(filter: string): SelectedFilter {
    const filterValues = {} as Record<FilterKeys, string[] | undefined>

    // Manually parse the filter string to handle encoded commas correctly
    // queryString.parse decodes values automatically, which breaks filter values containing commas
    const pairs = filter.split('&')
    pairs.forEach(pair => {
      const equalIndex = pair.indexOf('=')
      if (equalIndex > 0) {
        const key = pair.substring(0, equalIndex)
        const encodedValues = pair.substring(equalIndex + 1)

        // Split by comma (our delimiter) BEFORE decoding
        // This preserves encoded commas (%2C) within individual filter values
        const values = encodedValues
          .split(',')
          .map(v => {
            try {
              return decodeURIComponent(v.trim())
            } catch {
              return v.trim()
            }
          })
          .filter(v => v.length > 0)

        if (values.length > 0) {
          filterValues[key] = values
        }
      }
    })

    return new SelectedFilter(filterValues)
  }

  public static checkEquality(left: SelectedFilter, right: SelectedFilter): boolean {
    return isEqual<SelectedFilter>(left, right)
  }

  public static getColorFromUrl(): string {
    const {
      location: { pathname },
    } = window
    return getColorFromUrl(pathname)
  }

  /**
   * Creates a new SelectedFilter instance from scratch, containing only a single
   * subcollections value. This effectively resets all other filters.
   * @param productTypeValue The value for the subcollections filter.
   * @returns A new SelectedFilter instance.
   */
  public static initializeWithProductType(productTypeValue: string): SelectedFilter {
    return SelectedFilter.addValueToSelectedFilter(new SelectedFilter({}), EFilterKeys.SUBCOLLECTIONS, productTypeValue)
  }

  /**
   * FilterV2 format: No URL encoding, uses _ for = and __ for &
   * Example: colors_brown,beige__materials_linen
   * @param filterV2 The filterV2 query string
   * @returns A new SelectedFilter instance
   */
  public static getSelectedFilterFromQueryParamsV2(filterV2: string): SelectedFilter {
    const filterValues = {} as Record<FilterKeys, string[] | undefined>

    if (!filterV2) return new SelectedFilter(filterValues)

    // Split by __ (which replaces &)
    const pairs = filterV2.split('__')
    pairs.forEach(pair => {
      // Find first _ (which replaces =)
      const underscoreIndex = pair.indexOf('_')
      if (underscoreIndex > 0) {
        const wireKey = pair.substring(0, underscoreIndex)
        const valuesString = pair.substring(underscoreIndex + 1)

        // Split by comma (same as v1)
        const values = valuesString
          .split(',')
          .map(v => v.trim())
          .filter(v => v.length > 0)

        if (values.length > 0) {
          // Translate wire key (e.g. `producttype`) back to the internal
          // canonical key (e.g. `productType`) so downstream comparisons that
          // test `filter.name === EFilterKeys.PRODUCT_TYPE` keep working in
          // v2 mode without every consumer having to know about the wire shape.
          const canonicalKey = fromV2Key(wireKey)
          filterValues[canonicalKey] = values
        }
      }
    })

    return new SelectedFilter(filterValues)
  }

  /**
   * Generates filterV2 format string: No URL encoding, uses _ for = and __ for &
   * Example output: colors_brown,beige__materials_linen
   * @param selectedFilter The SelectedFilter instance to convert
   * @returns The filterV2 query string
   */
  public static getUpdatedSelectedFilterStringForWindowURLV2(selectedFilter: SelectedFilter): string {
    const filterString = Object.keys(selectedFilter)
      .map(key => {
        // No encoding for V2
        const values = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilter, key, false)
        if (values.length > 0) {
          // Translate the canonical key (e.g. `productType`) to its v2 wire
          // form (e.g. `producttype`) before serializing, so the URL we emit
          // is what the BFF and its v2 read parser expect. `toV2Key` is a
          // no-op for keys without an alias.
          return `${toV2Key(key)}_${values.join(',')}`
        }
        return undefined
      })
      .filter(value => value !== undefined)
      .join('__') // Use __ instead of &

    return filterString
  }

  public static getSelectedFilterFromQueryParamsWithV2Support(
    filter: string | null,
    filterV2: string | null,
    config: FilterVersionConfig = FILTER_VERSION_CONFIG,
  ): { selectedFilter: SelectedFilter; isV2Format: boolean } {
    const { enabled, versionPriority, queryUpdateVersion } = config

    const v1Available = enabled.v1 && filter
    const v2Available = enabled.v2 && filterV2

    // Both present — use versionPriority to decide read source
    if (v1Available && v2Available) {
      if (versionPriority === 'v2') {
        return {
          selectedFilter: SelectedFilter.getSelectedFilterFromQueryParamsV2(filterV2),
          isV2Format: queryUpdateVersion.v2 === 'v2',
        }
      }
      return {
        selectedFilter: SelectedFilter.getSelectedFilterFromQueryParams(filter),
        isV2Format: queryUpdateVersion.v1 === 'v2',
      }
    }
    // Only v2 present and enabled
    if (v2Available) {
      return {
        selectedFilter: SelectedFilter.getSelectedFilterFromQueryParamsV2(filterV2),
        isV2Format: queryUpdateVersion.v2 === 'v2',
      }
    }
    // Only v1 present and enabled
    if (v1Available) {
      return {
        selectedFilter: SelectedFilter.getSelectedFilterFromQueryParams(filter),
        isV2Format: queryUpdateVersion.v1 === 'v2',
      }
    }
    // No filter params — use default write version
    return {
      selectedFilter: new SelectedFilter({}),
      isV2Format: queryUpdateVersion.default === 'v2',
    }
  }
}

export enum EFilterKeys {
  COLORS = 'colors',
  SIZES = 'sizes',
  PRICES = 'prices',
  MATERIALS = 'materials',
  STYLES = 'styles',
  CONSTRUCTIONS = 'constructions',
  FABRICS = 'fabrics',
  PILE_HEIGHT = 'pile_height',
  CATEGORY_SWEATER = 'category_sweater',
  CATEGORIES = 'categories',
  DEPARTMENTS = 'departments',
  STONE_SHAPES = 'stone_shapes',
  DIAMOND_TYPES = 'diamond_types',
  JACKET_STYLES = 'jacket_styles',
  LENGTHS = 'lengths',
  WARMTHS = 'warmths',
  SUBCOLLECTIONS = 'subcollections',
  PRODUCT_TYPE = 'productType',
  ACTIVITY = 'activity',
  SLEEVE = 'sleeves',
  OCCASION = 'occasions',
}
